import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { areaPath, FIRST_PASSWORD_PATH } from "@/routes/areas";
import { useCurrentUser, useIsAuthenticated } from "@/stores/auth";
import type { Role } from "@/types/api";

/**
 * Pathless layout route that admits only signed-in users who have set their
 * own password.
 *
 * The attempted path is remembered in location state so that signing in
 * returns the user where they were going instead of to a generic landing page.
 *
 * The password gate is here rather than in each area so that an area added
 * later inherits it by being nested here -- it cannot be forgotten -- and so
 * that it holds for all three roles. It is a usability guard like RequireRole:
 * the server refuses such an account every route but the two it needs, so
 * getting past this would buy nothing but a screen full of 403s.
 */
export function RequireAuth() {
  const isAuthenticated = useIsAuthenticated();
  const user = useCurrentUser();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  /* A password somebody else chose is not a password. The path comparison is
     what keeps the screen itself reachable: it lives inside this guard,
     because it needs the access token like everything else here does. */
  if (user?.mustChangePassword && location.pathname !== FIRST_PASSWORD_PATH) {
    return <Navigate to={FIRST_PASSWORD_PATH} replace />;
  }

  return <Outlet />;
}

/**
 * Restricts a subtree to the listed roles.
 *
 * This is a usability guard, not the security boundary: the server enforces
 * the same rule on every endpoint. Hiding a page a user cannot use is simply
 * better than letting them click into a wall of 403s.
 */
export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const user = useCurrentUser();

  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/forbidden" replace />;

  return children;
}

/** Sends each role to the area that belongs to it. */
export function RoleLanding() {
  const user = useCurrentUser();

  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={areaPath[user.role]} replace />;
}
